import { ChatCompletionTool } from "openai/resources";
import { ObjectPKInput } from "../aws/DynamoDbApiTypes";
import { BaseDynamoClass, BaseDynamoFields, DynamoKey } from "./BaseDynamoClass";
import { UnderscoreKeys } from "../util/Types";

export type AiPromptSetProps = {
	analyseTopic: AiPrompt;
	createTitleAndSynopsisOptions: AiPrompt;
	identifyCharacters: AiPrompt;
	createStory: AiPrompt;
	regeneratePage: AiPrompt;
	writeDedication: AiPrompt;
	writeVisualDescriptions: AiPrompt;
	writeCoverVisualDescriptions: AiPrompt;
};

export type AiImagePromptSetProps = {
	createImage: AiImagePrompt;
	createCoverImage: AiImagePrompt;
};

export type AiPromptStringified = {
	name: string;
	generator: string;
	chatCompletionTool: ChatCompletionTool;
};

export type AiPrompt = {
	generator: AiPromptGenerator;
	chatCompletionTool: ChatCompletionTool;
};
export type AiImagePrompt = {
	generator: AiPromptGenerator;
};

export type AiPromptGenerator = Function;

export type AiPromptSetKeyInput = {
	promptSetId: string;
};

export type AiPromptSetFields<T extends AiPromptSetProps | AiImagePromptSetProps> = BaseDynamoFields & {
	promptSetId: string | undefined;
	name: string;
	description: string;
	example: string;
	prompts: T;
};
export default class AiPromptSet<
	T extends AiPromptSetProps | AiImagePromptSetProps,
	TFields extends AiPromptSetFields<T> = AiPromptSetFields<T>,
> extends BaseDynamoClass<AiPromptSetFields<T>> {
	_promptSetId: string | undefined;
	_name!: string;
	_description!: string;
	_example!: string;
	_prompts!: T;
	override _className: string = "AiPromptSet";
	override _deserializeClassName: string = "AiPromptSet";

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [...super.getFieldList(), "_promptSetId", "_name", "_description", "_example", "_prompts"];
	}
	constructor(props: Partial<TFields>) {
		super(props);
	}
	get promptSetId(): string | undefined {
		return this.checkFieldType<"promptSetId">(this._promptSetId);
	}
	set promptSetId(value: string | undefined) {
		this._promptSetId = this.checkFieldTypeAndMark<"promptSetId">("_promptSetId", value);
	}

	get name(): string {
		return this.checkFieldType<"name">(this._name);
	}
	set name(value: string) {
		this._name = this.checkFieldTypeAndMark<"name">("_name", value);
	}

	get description(): string {
		return this.checkFieldType<"description">(this._description);
	}
	set description(value: string) {
		this._description = this.checkFieldTypeAndMark<"description">("_description", value);
	}

	get example(): string {
		return this.checkFieldType<"example">(this._example);
	}
	set example(value: string) {
		this._example = this.checkFieldTypeAndMark<"example">("_example", value);
	}

	get prompts(): T {
		return this.checkFieldType<"prompts">(this._prompts);
	}
	set prompts(value: T) {
		this._prompts = this.checkFieldTypeAndMark<"prompts">("_prompts", value);
	}

	stringifyFunction(fnc: Function) {
		return JSON.stringify(fnc, (key, value) => {
			let fnBody;
			if (value instanceof Function || typeof value == "function") {
				fnBody = value.toString();
				if (fnBody.length < 8 || fnBody.substring(0, 8) !== "function") {
					//this is ES6 Arrow Function
					return "_NuFrRa_" + fnBody;
				}
				return fnBody;
			}
			if (value instanceof RegExp) {
				return "_PxEgEr_" + value;
			}
			return value;
		});
	}
	parseFunction(fnc: string) {
		return JSON.parse(fnc, (key, value) => {
			const prefix = value.substring(0, 8);
			if (prefix === "function") {
				return eval("(" + value + ")");
			}
			if (prefix === "_PxEgEr_") {
				return eval(value.slice(8));
			}
			if (prefix === "_NuFrRa_") {
				return eval(value.slice(8));
			}
			return value;
		});
	}
	getPKPrefix(): string {
		return "PROMPTSET#";
	}
	getSKPrefix(props: object | undefined): string {
		return "PROMPTSET#";
	}
	getPK(): string {
		return this.getPKPrefix() + this.promptSetId;
	}
	getSK(): string {
		return this.getSKPrefix(this) + this.promptSetId;
	}
	getParentKey(): DynamoKey | undefined {
		return undefined;
	}
	getParentKeyInput(): ObjectPKInput | undefined {
		return undefined;
	}
	getParentTableName(): string | undefined {
		return undefined;
	}
	generateKey(input: any): DynamoKey {
		return {
			PK: this.generatePK(input),
			SK: this.getSK(),
		};
	}
	generatePK(input: AiPromptSetKeyInput): string {
		return this.getPKPrefix() + input.promptSetId;
	}
	generateGSIPK(index: number, key: object): string {
		throw new Error("Method not implemented.");
	}
	generateGSISK(index: number, key: object | undefined): string {
		throw new Error("Method not implemented.");
	}
	getGlobalIndices(): object {
		return {};
	}
	getColumnsForExport(): string[] {
		throw new Error("Method not implemented.");
	}
	getObjectForExport(): object {
		throw new Error("Method not implemented.");
	}
}
